import React from "react";
import { motion } from "framer-motion";
import { Button } from "@src/stories/elements/Button/Button";
import AnimatedPulse from "../../AnimatedPulse/AnimatedPulse";
import Blush from "../../Blush/Blush";
import { TimelineDot } from "../Components/TimelineDot";
import { useTimelineScroll } from "../hooks/useTimelineScroll";

interface MobileTimelineLayoutProps {
    items: Array<{
        title: string;
        description: string;
        icon?: string;
    }>;
    rightSideContent: {
        title: string;
        description?: string;
        buttonText: string;
        buttonUrl: string;
        pulseSettings: any;
    };
    scrollProgress: number;
}

export const MobileTimelineLayout: React.FC<MobileTimelineLayoutProps> = ({ 
    items, 
    rightSideContent, 
    scrollProgress 
}) => {
    const { currentItemIndex, scrollOffset, timelineRef, itemRefs } = useTimelineScroll({
        itemCount: items.length,
        offset: 100, // Offset to trigger earlier
        threshold: 0.3
    });

    return (
        <div ref={timelineRef} className="mobile-timeline-layout">
            {/* Top section - right side content from desktop */}
            <div className="mobile-top-section px-4 py-8 mb-8">
                <div className="right-content-mobile relative">
                    {/* Background effects */}
                    <div className="blush-background absolute inset-0 z-[-1] pointer-events-none">
                        <div className="absolute w-full h-full bg-gradient-radial from-[#f9f6f0]/40 via-[#f1ede4]/30 to-transparent opacity-70"></div>
                        <div className="absolute w-[140%] h-[140%] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                                       bg-gradient-radial from-[#f1ede4]/30 via-[#e9e3d6]/20 to-transparent opacity-60"></div>
                    </div>
                    
                    <div className="pulse-container absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-full w-full">
                        <AnimatedPulse settings={rightSideContent.pulseSettings} />
                    </div>
                    
                    <div className="content-container relative z-10 flex flex-col gap-5 py-6">
                        <h2 className="text-[28px] font-bold text-gray-900 leading-tight">{rightSideContent.title}</h2>
                        <div className="text-[15px] leading-relaxed text-gray-700">
                            {rightSideContent.description && (
                                <p dangerouslySetInnerHTML={{ __html: rightSideContent.description }} />
                            )}
                        </div>
                        <div className="mt-3">
                            <Button 
                                style="primary" 
                                icon="right-arrow-alt" 
                                label={rightSideContent.buttonText} 
                                reverse={true}
                                link={rightSideContent.buttonUrl}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Timeline items with vertical line indicator */}
            <div className="mobile-timeline-items relative">
                {/* Vertical timeline line */}
                <div className="timeline-line-container absolute left-6 top-0 bottom-0 w-[2px] z-0">
                    <div className="timeline-line w-full h-full bg-[#E3DBC8]"></div>
                    {/* Progress indicator */}
                    <div 
                        className="timeline-progress absolute top-0 left-0 w-full bg-[#899B30] transition-all duration-300 ease-out"
                        style={{
                            height: `${(currentItemIndex + 1) / items.length * 100}%`
                        }}
                    ></div>
                </div>

                {/* Timeline items */}
                <div className="timeline-items-list space-y-12 pb-16">
                    {items.map((item, index) => (
                        <motion.div 
                            key={index}
                            ref={(el) => {itemRefs.current[index] = el;}}
                            className="timeline-item flex items-start relative z-10"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true, margin: "-50px" }}
                        >
                            {/* Timeline dot using reusable component */}
                            <TimelineDot
                                isActive={index <= currentItemIndex}
                                index={index}
                                size="medium"
                                showTrail={index === currentItemIndex}
                                trailProgress={scrollOffset * 0.01}
                            />

                            {/* Item content */}
                            <div className="timeline-item-content flex-1 pl-4 pr-4">
                                <div className="relative">
                                    <Blush size={300} color="#EDD5C9" />
                                    
                                    <div className="relative z-10 max-w-md">
                                        {item.icon && (
                                            <div className="mb-3">
                                                <img
                                                    src={item.icon}
                                                    alt={item.title}
                                                    className="w-8 h-8"
                                                />
                                            </div>
                                        )}
                                        <h3
                                            className="mb-3 text-[20px] font-semibold text-gray-900 leading-tight"
                                            dangerouslySetInnerHTML={{ __html: item.title }}
                                        ></h3>
                                        <p className="text-[15px] leading-relaxed text-gray-700">{item.description}</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MobileTimelineLayout;